import styled from '@emotion/styled';
import { Checkbox, message, Space } from 'antd';
import { Fragment, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import TradeProduct from './TradeProduct';
import { TagProduct, ProductTagList } from 'beisi/TagProduct';
import StatusButton from 'components/StatusButton';
import RefundDrawer from 'views/Refund/components/RefundDrawer';
import useModal from 'hooks/useModal';
import { useTradeListContext } from './TradeContext';
import { queryReturnListV2 } from 'services/api/refund';
import { useMemoizedFn } from 'ahooks';
import { TradeReturnStatus } from './TradeReturnStatus';

export interface TradeReturnProductProps {
  sn: string;
  conversation: Conversation.Conversation;
  data?: Conversation.Trade.ReturnProduct[];
  trade: Conversation.Trade.TradeWithLogistics;
  checked?: boolean;
  onCheckedChange?: (checked?: boolean) => void;
  reviewId?: string;
  exportMode?: boolean;
  lazyScrollContainer?: Element | null;
}

const TradeReturnProduct = (props: TradeReturnProductProps) => {
  const {
    sn,
    reviewId,
    data = [],
    exportMode = false,
    trade,
    conversation,
    lazyScrollContainer,
    checked,
    onCheckedChange,
  } = props;

  const { externalTradeId, externalShopId, platform } = trade;

  const { t } = useTranslate();

  const returnTrackingNumber = data?.[0]?.returnTrackingNumber;

  const { refreshTrade } = useTradeListContext();

  const { visible, show, hide, meta } = useModal<any>();

  const onUpdate = useMemoizedFn(() =>
    refreshTrade(
      {
        page_no: 1,
        page_size: 1,
        external_buyer_id: conversation.externalBuyerId,
        internal_store_id: conversation.storeId,
        external_trade_ids: externalTradeId,
      },
      platform,
    ).catch(() => {}),
  );

  const handleDetailClick = useMemoizedFn(() => {
    if (meta) {
      return Promise.resolve().then(() => {
        show(meta);
      });
    }
    return queryReturnListV2({
      returnSn: sn,
      platform,
      page: {
        pageNo: 1,
        pageSize: 1,
      },
    })
      .then((res) => {
        if (res.returnOrderDataList.length) {
          show(res.returnOrderDataList[0]);
        } else {
          return Promise.reject(new Error('no result'));
        }
      })
      .catch(() => {
        message.error(t('common.feedback.fetchFailed'));
      });
  });

  const lastRefundReason = useMemo(() => {
    const lastItem = data.findLast((item) => {
      return item.refundReason && item.refundReason.length > 0;
    });
    return lastItem?.refundReason?.[0];
  }, [data]);

  return (
    <div key={`${sn}_${reviewId}`}>
      <ReturnBar>
        {checked !== undefined && (
          <Checkbox
            checked={checked}
            onChange={(e) => {
              if (onCheckedChange) {
                onCheckedChange(e.target.checked);
              }
            }}
          />
        )}
        <div className="w-full">
          <div className="flex justify-between items-center">
            <div>
              <span>{t('domain.order.field.returnId')}</span>
              <span className="ml-1">{sn}</span>
            </div>
            {platform !== 'lazada' && (
              <StatusButton type="link" onClick={handleDetailClick}>
                {t('scene.chatWorkspace.orderTab.returnRefund.action.viewDetail')}
              </StatusButton>
            )}
            {meta && (
              <RefundDrawer
                type="refund"
                visible={visible}
                platform={platform}
                conversation={conversation}
                row={meta}
                onClose={() => hide()}
                onUpdate={onUpdate}
                refreshList={onUpdate}
              />
            )}
          </div>
          {returnTrackingNumber ? (
            <div>
              <span>{t('domain.order.logistics.field.trackingNumber')}</span>
              <span className="ml-1">{returnTrackingNumber}</span>
            </div>
          ) : null}
        </div>
      </ReturnBar>
      {data.map((item) => {
        const { itemId, itemSku: sku, index } = item;
        const returnOrderItemId = item.refundReason?.[0]?.returnOrderItemId ?? '';
        return (
          <Fragment key={`${itemId}-${sku}`}>
            {platform === 'lazada' && returnOrderItemId ? (
              <ReturnBar>
                <Space>
                  <span>{t('scene.chatWorkspace.orderTab.returnRefund.label.returnItemId')}</span>
                  <span>{returnOrderItemId}</span>
                </Space>
              </ReturnBar>
            ) : null}
            <Product
              exportMode={exportMode}
              product={item}
              conversation={conversation}
              productIndex={index}
              externalShopId={externalShopId}
              showReturn
              lazyScrollContainer={lazyScrollContainer}
            />
            <ProductTagList trade={trade} productData={item} />
          </Fragment>
        );
      })}
      <TagProduct
        productData={data}
        border={['top', 'bottom']}
        className="translate-y-[-5px] py-[8px]"
        trade={trade}
        conversation={conversation}
        sourceId={reviewId || sn}
      />
      {lastRefundReason && (
        <TradeReturnStatus
          sn={sn}
          conversation={conversation}
          trade={trade}
          refundReason={lastRefundReason}
          exportMode={exportMode}
          data={data}
        />
      )}
    </div>
  );
};

const ReturnBar = styled.div`
  background: #f0f7ff;
  min-height: 28px;
  padding: 0 12px !important;
  display: flex;
  align-items: center !important;
  > label {
    margin-right: 12px;
  }
`;
const Product = styled(TradeProduct)`
  margin: 5px -5px;
`;

export default TradeReturnProduct;
